/** Policy v2 claims language — the officer-facing vocabulary.
 *
 * The API speaks obligation codes (E1…M3), source types (REGISTRY…INTERNAL)
 * and four-way precise failure reasons; the officer reads claims. One map,
 * shared by every surface that renders obligation rows (ChasePane, fund
 * dashboard missing-document inventory, rollup drill-downs).
 *
 * Source of truth: _POLICY-V2-FULL.md (Ale-ruled 2026-07-07) — the eight
 * obligations in three families. Codes unknown to this map render as-is
 * (a future catalog migration must extend the map alongside).
 */

/** Obligation identity: code → what the policy demands, in officer words. */
export const OBLIGATION_LABELS: Record<string, string> = {
  E1: 'Constitutional document',
  E2: 'Register extract',
  B1: 'Position balance',
  B2: 'Position balance (committed)',
  B3: 'Instrument balance',
  M1: 'Trade movement',
  M2: 'Trade cash settlement',
  M3: 'Trade authorization',
}

/** One-line explanation per obligation — what the demand means. */
export const OBLIGATION_EXPLANATIONS: Record<string, string> = {
  E1: 'proof that the entity or asset legally exists (its constituting document)',
  E2: 'an official register confirms the entity or asset',
  B1: 'an outside statement confirms the units the record holds',
  B2: 'an outside statement confirms the commitment — committed, drawn and undrawn must agree',
  B3: "an outside statement confirms the instrument's total outstanding",
  M1: 'a document evidences the trade happened — units moved on a date',
  M2: 'a bank-side record confirms the cash leg of the trade',
  M3: 'an internal resolution shows the trade was authorized',
}

/** Accepted source types → plain officer language. */
export const SOURCE_LABELS: Record<string, string> = {
  REGISTRY: 'official registry extract',
  NOTARY: 'notarized original or certified copy',
  PARTY: 'signed counterparty document',
  ISSUER: 'issuer statement (GP, administrator or fund notice)',
  AGENT: 'independent agent statement (servicer, facility agent, custodian)',
  BANK: 'bank or cash-side record',
  INTERNAL: 'internal resolution (IC, board or GP)',
}

/** Precise gate failures → why the document fell short. */
export const FAILURE_LABELS: Record<string, string> = {
  no_such_claim: 'the document makes no claim of this kind',
  wrong_object: 'the document does not reference this record item',
  source_not_accepted: "the document's source is not accepted for this obligation",
  not_executed: 'the document is not signed by every party',
  content_mismatch: "the document's stated values disagree with the record",
  signoff_rejected: 'an officer declined this document at sign-off',
}

/** evidence_status.status → officer chip label ("pending_signoff" must
 *  never render as a raw enum — atlas review 2026-07-27). */
export const STATUS_LABELS: Record<string, string> = {
  open: 'open',
  requested: 'requested',
  pending_signoff: 'pending sign-off',
  satisfied: 'satisfied',
  waived: 'waived',
}

export function statusLabel(status: string | null | undefined): string {
  if (!status) return ''
  return STATUS_LABELS[status] ?? status.replace(/_/g, ' ')
}

export function obligationLabel(code: string | null | undefined): string {
  if (!code) return 'Evidence'
  return OBLIGATION_LABELS[code] ?? code
}

export function obligationExplanation(code: string | null | undefined): string | null {
  if (!code) return null
  return OBLIGATION_EXPLANATIONS[code] ?? null
}

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source
}

/** "official registry extract, issuer statement (…) or independent agent
 *  statement (…)" — the accepted-sources sentence fragment. */
export function acceptedSourcesLabel(sources: readonly string[] | null | undefined): string {
  if (!sources || sources.length === 0) return ''
  const labels = sources.map(sourceLabel)
  if (labels.length === 1) return labels[0]
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`
}

export function failureLabel(failure: string): string {
  return FAILURE_LABELS[failure] ?? failure.replace(/_/g, ' ')
}
